import type { CgDocType } from '../ocr/ocrResponseTypes';
import type { ExtractedFieldEntry, UploadedDocResult } from '../store/useFormCGIAStore';

/**
 * Champs d'identité portés par une pièce d'identité — ré-attribués ensemble
 * quand on départage titulaire / co-titulaire.
 */
const ACQUEREUR_IDENTITY_FIELDS = [
  'nom',
  'prenom',
  'nom_usage',
  'civilite',
  'date_naissance',
  'lieu_naissance',
  'departement_naissance',
  'pays_naissance',
  'numero_document',
  'type_document',
] as const;

const normName = (v: unknown): string =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const docFieldStr = (doc: UploadedDocResult | undefined, key: string): string | null => {
  const v = (doc?.ocrResponse?.fields as Record<string, { value?: unknown }> | undefined)?.[key]?.value;
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
};

interface NameRef {
  nom: string | null;
  prenom: string | null;
}

const scoreVsRef = (doc: UploadedDocResult, ref: NameRef): number => {
  // Le nom de référence (cession/mandat) peut être le nom de naissance OU le nom
  // d'usage de la CNI (FX-187-XA : cession « ROGER », CNI « MOUSSIER » ép. ROGER).
  const nomOk = Boolean(
    ref.nom &&
      [docFieldStr(doc, 'nom'), docFieldStr(doc, 'nom_usage')].some((v) => v && normName(ref.nom) === normName(v)),
  );
  // Prénom prioritaire (hébergement intrafamilial : nom de famille commun).
  const prenomOk = Boolean(
    ref.prenom && docFieldStr(doc, 'prenom') && normName(ref.prenom) === normName(docFieldStr(doc, 'prenom')),
  );
  return (prenomOk ? 2 : 0) + (nomOk ? 1 : 0);
};

const buildTitulaireRefs = (uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>): NameRef[] =>
  [
    {
      nom: docFieldStr(uploadedDocs.cerfa_cession, 'acheteur_nom'),
      prenom: docFieldStr(uploadedDocs.cerfa_cession, 'acheteur_prenom'),
    },
    {
      nom: docFieldStr(uploadedDocs.mandat, 'mandant_nom'),
      prenom: docFieldStr(uploadedDocs.mandat, 'mandant_prenom'),
    },
  ].filter((r) => r.nom || r.prenom);

/**
 * Résout, quand DEUX pièces d'identité coexistent, laquelle est celle du
 * TITULAIRE acquéreur et laquelle est celle du CO-TITULAIRE, par croisement avec
 * l'acheteur de la cession ET le mandant du mandat (méthodo François). Retourne
 * `null` si on ne peut pas trancher (une seule CNI, aucune référence, ou aucune
 * ne matche mieux que celle déjà dans le slot principal).
 *
 * `swap = true` signale que la CNI de l'ACQUÉREUR est actuellement rangée dans
 * le slot `piece_identite_hebergeur` (mauvais slot), et que celle du
 * co-titulaire occupe le slot principal `piece_identite` — il faut donc les
 * inverser à l'affichage.
 */
export const resolveAcquereurCni = (
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>,
): { acquereur: UploadedDocResult; cotitulaire: UploadedDocResult; swap: boolean } | null => {
  const pi = uploadedDocs.piece_identite;
  const piHeb = uploadedDocs.piece_identite_hebergeur;
  if (!pi || !piHeb) return null;

  const refs = buildTitulaireRefs(uploadedDocs);
  if (refs.length === 0) return null;

  const scorePi = Math.max(0, ...refs.map((r) => scoreVsRef(pi, r)));
  const scoreHeb = Math.max(0, ...refs.map((r) => scoreVsRef(piHeb, r)));

  // Le slot hébergeur matche STRICTEMENT mieux le titulaire → c'est LUI
  // l'acquéreur ; le slot principal porte en fait le co-titulaire → swap.
  if (scoreHeb > scorePi) {
    return { acquereur: piHeb, cotitulaire: pi, swap: true };
  }
  // Sinon le slot principal est (au moins) aussi bon : on le garde acquéreur.
  return { acquereur: pi, cotitulaire: piHeb, swap: false };
};

/**
 * CNI du CO-TITULAIRE ACQUÉREUR détectée dans le PREMIER LOT (dropzone
 * principale) : parmi les deux slots CNI du dossier, celle dont le nom (et le
 * prénom, quand les deux côtés le portent) matche le co-titulaire déclaré sur
 * le 13750 ou la cession. Sert de REPLI au slot `piece_identite_cotitulaire`
 * (zone dédiée, type imposé) pour la règle piece_identite_cotitulaire_incomplete :
 * sans ce repli, une CNI co-titulaire déposée avec le reste du dossier
 * déclenchait « aucune pièce déposée » alors que la checklist affichait
 * « Rattachée au co-titulaire » (re-test EQ-598-TE, 2026-07-24).
 *
 * Garde homonymes (couples au même nom de famille) : si le cerfa ET la CNI
 * portent un prénom, ils doivent matcher — sinon la CNI de l'acquéreur
 * passerait pour celle de son co-titulaire homonyme.
 */
export const resolveCotitulairePiFromBatch = (
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>,
): UploadedDocResult | null => {
  const refs: NameRef[] = [
    {
      nom: docFieldStr(uploadedDocs.cerfa_demande_immat, 'cotitulaire_nom'),
      prenom: docFieldStr(uploadedDocs.cerfa_demande_immat, 'cotitulaire_prenom'),
    },
    {
      nom: docFieldStr(uploadedDocs.cerfa_cession, 'cotitulaire_nom'),
      prenom: docFieldStr(uploadedDocs.cerfa_cession, 'cotitulaire_prenom'),
    },
  ].filter((r) => r.nom);
  if (refs.length === 0) return null;

  // La 2e CNI (slot hébergeur) est le cas usuel ; le slot principal couvre le
  // cas swap où la CNI du co-titulaire y a été rangée par erreur.
  const candidates = [uploadedDocs.piece_identite_hebergeur, uploadedDocs.piece_identite].filter(
    (d): d is UploadedDocResult => Boolean(d),
  );

  const matchesRef = (candidate: UploadedDocResult, ref: NameRef): boolean => {
    const nomOk = Boolean(
      ref.nom &&
        [docFieldStr(candidate, 'nom'), docFieldStr(candidate, 'nom_usage')].some(
          (v) => v && normName(ref.nom) === normName(v),
        ),
    );
    if (!nomOk) return false;
    const candidatePrenom = docFieldStr(candidate, 'prenom');
    if (ref.prenom && candidatePrenom && normName(ref.prenom) !== normName(candidatePrenom)) return false;
    return true;
  };

  return candidates.find((candidate) => refs.some((ref) => matchesRef(candidate, ref))) ?? null;
};

/**
 * Dérive l'état des DEUX checkboxes co-titulaire à partir des données extraites :
 *  - `acheteurActif` : co-titulaire ACHETEUR (nouveau co-propriétaire) — piloté
 *    par `cobuyer_present` (détecté sur le 13750/cession, ou coché à la main).
 *  - `vendeurActif` : co-titulaire VENDEUR (C.4.1 carte grise) — détecté sur la
 *    carte grise brute (le C.4.1 est exclu du merge), surchargeable via
 *    `vendeur_cotitulaire_present`.
 *  - `vendeurNom` : nom du co-titulaire vendeur si lisible (pour l'affichage).
 */
export const deriveCotitulaireFlags = (
  extractedFields: Record<string, ExtractedFieldEntry>,
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>,
): { acheteurActif: boolean; vendeurActif: boolean; vendeurNom: string | null } => {
  // Auto-détecté sur le 13750 : les champs `cotitulaire_*` fusionnés viennent du
  // Cerfa de demande d'immat (le C.4.1 de la carte grise est exclu du merge), ils
  // désignent donc le co-titulaire ACHETEUR. La case doit se cocher seule quand le
  // cas est détecté (parité avec /review). `cobuyer_present` reste la surcharge
  // manuelle (coche/décoche par l'agent) et prime sur la détection.
  const acheteurDetected =
    extractedFields['cotitulaire_present']?.field?.value === true ||
    extractedFields['cotitulaire_present']?.field?.value === 'true' ||
    Boolean(extractedFields['cotitulaire_nom']?.field?.value);
  const acheteurOverride = extractedFields['cobuyer_present']?.field?.value;
  const acheteurActif =
    acheteurOverride === 'true' || acheteurOverride === true
      ? true
      : acheteurOverride === 'false' || acheteurOverride === false
        ? false
        : acheteurDetected;

  const cgFields = (uploadedDocs?.carte_grise?.ocrResponse?.fields ?? {}) as Record<string, { value?: unknown }>;
  const rawVendeurNom = cgFields['cotitulaire_nom']?.value;
  const c41 = cgFields['cotitulaire_c41']?.value as { nom?: string } | undefined;
  const vendeurNom = (typeof rawVendeurNom === 'string' && rawVendeurNom) || c41?.nom || null;
  const vendeurDetected = Boolean(vendeurNom) || cgFields['cotitulaire_present']?.value === true;
  const vendeurOverride = extractedFields['vendeur_cotitulaire_present']?.field?.value;
  const vendeurActif =
    vendeurOverride === 'true' || vendeurOverride === true
      ? true
      : vendeurOverride === 'false' || vendeurOverride === false
        ? false
        : vendeurDetected;

  return { acheteurActif, vendeurActif, vendeurNom };
};

/**
 * Variante /review : dérive les mêmes valeurs de co-titulaire (acheteur/vendeur)
 * à partir des données de la page review (extractions brutes + champs agrégés),
 * pour afficher EXACTEMENT le même bloc que form-cgia (parité Review = Form).
 */
export const deriveReviewCotitulaire = (
  extractions: Array<{ docTypeDetected: string; fields: unknown }>,
  aggregatedFields: Array<{ fieldKey: string; value: unknown }>,
): { acheteurActif: boolean; vendeurActif: boolean; vendeurNom: string | null; cotitulaireLabel?: string } => {
  const aggVal = (k: string): unknown => aggregatedFields.find((f) => f.fieldKey === k)?.value;
  const acheteurRaw = aggVal('cotitulaire_present');
  const acheteurActif = acheteurRaw === true || acheteurRaw === 'true' || Boolean(aggVal('cotitulaire_nom'));

  const cg = extractions.find((e) => e.docTypeDetected === 'carte_grise');
  const cgFields = (cg?.fields ?? {}) as Record<string, { value?: unknown }>;
  const rawVendeurNom = cgFields['cotitulaire_nom']?.value;
  const c41 = cgFields['cotitulaire_c41']?.value as { nom?: string } | undefined;
  const vendeurNom = (typeof rawVendeurNom === 'string' && rawVendeurNom) || c41?.nom || null;
  const vendeurActif = Boolean(vendeurNom) || cgFields['cotitulaire_present']?.value === true;

  const coNom = aggVal('cotitulaire_nom');
  const coPrenom = aggVal('cotitulaire_prenom');
  const cotitulaireLabel =
    acheteurActif && (coNom || coPrenom) ? `${String(coPrenom ?? '')} ${String(coNom ?? '')}`.trim() : undefined;

  return { acheteurActif, vendeurActif, vendeurNom, cotitulaireLabel };
};

/**
 * Renvoie une copie de `extractedFields` dont l'identité de l'ACHETEUR
 * (nom/prénom/naissance…) provient de la CNI réellement rattachée au titulaire
 * (cf. resolveAcquereurCni). Sans ça, l'acheteur affiché était celui du slot
 * `piece_identite` brut — pas forcément le titulaire (cas DY-565-KJ : CARCENAC
 * affichée à la place de BEAUSSE). No-op si aucun départage n'est nécessaire.
 */
export const reconcileAcquereurExtractedFields = (
  extractedFields: Record<string, ExtractedFieldEntry>,
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>,
): Record<string, ExtractedFieldEntry> => {
  const resolved = resolveAcquereurCni(uploadedDocs);
  if (!resolved || !resolved.swap) return extractedFields;

  const next = { ...extractedFields };
  for (const key of ACQUEREUR_IDENTITY_FIELDS) {
    const value = docFieldStr(resolved.acquereur, key);
    if (value == null) continue;
    next[key] = {
      fieldKey: key,
      field: { value, confidence: 1, source: 'extracted' },
      sourceDocType: 'piece_identite',
    };
  }
  return next;
};
